import { Skeleton } from '@vizit/atoms';
import { ScoreRange } from '@vizit/constants';
import { cva } from '@vizit/theme';
import { getScoreRange } from '@vizit/utils';
import * as React from 'react';
import { Text, TextSize, TextWeight } from '../text';

interface ScoreBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Score value to display.
   *
   * @optional
   * @type number | null
   * @default null
   */
  score: number | null;
  /**
   * Whether is loading.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isLoading?: boolean;
  /**
   * Whether the score should be omitted.
   *
   * @optional
   * @type boolean
   * @default false
   */
  omitScore?: boolean;
}

const classes = cva({
  base: 'inline-flex items-center justify-center p-1 h-18 min-w-[48px] shadow-no-top bg-white',
  variants: {
    range: {
      [ScoreRange.NotApplicable]: 'text-score-not-applicable',
      [ScoreRange.VeryLow]: 'text-score-very-low',
      [ScoreRange.Low]: 'text-score-low',
      [ScoreRange.Moderate]: 'text-score-moderate',
      [ScoreRange.High]: 'text-score-high',
      [ScoreRange.VeryHigh]: 'text-score-very-high',
      [ScoreRange.Unscored]: '',
    },
  },
  defaultVariants: {
    range: ScoreRange.NotApplicable,
  },
});

/**
 * A badge to display a status or label.
 *
 * @param {ScoreBadgeProps} - The props to use for the badge.
 */
const ScoreBadge = React.forwardRef<HTMLDivElement, ScoreBadgeProps>((props, forwardedRef) => {
  const { children, className, score, isLoading = false, omitScore = false, ...other } = props;

  return (
    <div
      ref={forwardedRef}
      className={classes({ className, range: getScoreRange(score) ?? ScoreRange.NotApplicable })}
      {...other}
    >
      {/* Loading state */}
      {isLoading && <Skeleton className="h-8 w-14" isLoaded={!isLoading} variant="rounded" />}
      {/* No score/Omit score state */}
      {(omitScore || score === null) && !isLoading && <div className="rounded-full h-8 w-14 bg-gray-utility-300" />}
      {/* Score state */}
      {!isLoading && !omitScore && (
        <Text
          elementType="span"
          weight={TextWeight.Bold}
          size={TextSize.Lg}
          showEllipsis={false}
          className="text-inherit"
        >
          {score}
        </Text>
      )}
    </div>
  );
});

export type { ScoreBadgeProps };
export { ScoreBadge };
